import {
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { AdService } from "../../services";
import { asQuery } from "../../services/common";
import { AD_STALE, DASH_STALE, AdsPage } from "./shared";

// ── Public lists ─────────────────────────────────────────────────────────

// Single-page ad search — used by the home/search carousels (fixed limit).
// `params` is the full search body (type, page, limit, filters, location).
export function useAdSearch(params: any, enabled = true) {
  return useQuery({
    queryKey: ["ads", "search", params],
    queryFn: () => asQuery<AdsPage>(AdService.search(params)),
    enabled,
    staleTime: AD_STALE,
    placeholderData: keepPreviousData,
  });
}

// Paginated "load more" ad search. `params` must NOT include `page` — the
// page index is supplied by React Query's pageParam (0-based). `more` from the
// last page decides whether another page exists.
export function useAdSearchInfinite(params: any, enabled = true) {
  return useInfiniteQuery({
    queryKey: ["ads", "search-infinite", params],
    queryFn: ({ pageParam }) =>
      asQuery<AdsPage>(AdService.search({ ...params, page: pageParam })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.more ? allPages.length : undefined,
    enabled,
    staleTime: AD_STALE,
    placeholderData: keepPreviousData,
  });
}

// A seller's ads filtered by bid-state tab (open-for-bids / upcoming / sold).
export function useSellerAds(userId?: string, type?: string, location?: any) {
  return useQuery({
    queryKey: ["ads", "public-by-user", userId, type, location],
    queryFn: () =>
      asQuery<any[]>(
        AdService.getPublicAdsByUserIdAndType({ userId, type, location }),
      ),
    enabled: !!userId && !!type,
    staleTime: AD_STALE,
    placeholderData: keepPreviousData,
  });
}

// ── Detail ───────────────────────────────────────────────────────────────

// Full artwork details ({ ad, seller }). Keyed on id only; the `body` (viewer
// id for the server-side view counter) and token ride along but aren't part of
// the cache identity.
export function useAdDetails(id?: string, body: any = {}, token = "") {
  return useQuery({
    queryKey: ["ads", "details", id],
    queryFn: () =>
      asQuery<any>(AdService.getDetailsById(id as string, body, token)),
    enabled: !!id,
    staleTime: AD_STALE,
  });
}

// Similar artworks for the detail page's "you may also like" rail.
export function useSimilarAds(params: any, enabled = true) {
  return useQuery({
    queryKey: ["ads", "similar", params],
    queryFn: () => asQuery<any[]>(AdService.getSimilarAds(params)),
    enabled,
    staleTime: AD_STALE,
  });
}

// ── The signed-in seller's own listings (dashboard tables) ────────────────
export function useMyListings(token: string | undefined, params: any) {
  return useQuery({
    queryKey: ["ads", "mine", token, params],
    queryFn: () => asQuery<any>(AdService.getMine(token as string, params)),
    enabled: !!token,
    staleTime: DASH_STALE,
    placeholderData: keepPreviousData,
  });
}

export function useMyOpenListings(token: string | undefined, params: any) {
  return useQuery({
    queryKey: ["ads", "mine-open", token, params],
    queryFn: () => asQuery<any>(AdService.getMineOpen(token as string, params)),
    enabled: !!token,
    staleTime: DASH_STALE,
    placeholderData: keepPreviousData,
  });
}
